import {
  createContext,
  Fragment,
  useContext,
  useEffect,
  useState,
} from "react";
import ReloadWarningPopup from "../components/ReloadWarningPopup";

interface ModalContextType {
  create: (id: string, content: React.ReactNode | null) => void;
  destroy: (id: string) => void;
}

const ModalContext = createContext<ModalContextType | null>(null);

export const ModalProvider = ({ children }: { children: React.ReactNode }) => {
  const [content, setContent] = useState<Record<string, React.ReactNode>>({});

  useEffect(() => {
    const abortController = new AbortController();
    window.addEventListener(
      "keydown",
      (e) => {
        if (e.key === "Escape") {
          const key = Object.keys(content).pop();
          if (key) {
            destroy(key);
          }
        }
      },
      {
        signal: abortController.signal,
      }
    );

    return () => {
      abortController.abort();
    };
  }, [document, content]);

  const create = (id: string, content: React.ReactNode | null) => {
    setContent((prev) => ({ ...prev, [id]: content }));
  };

  const destroy = (id: string) => {
    setContent((prev) => {
      const { [id]: _, ...rest } = prev;
      return rest;
    });
  };

  return (
    <ModalContext.Provider
      value={{
        create,
        destroy,
      }}
    >
      {children}

      {Object.entries(content).map(([id, content]) => (
        <Fragment key={id}>{content}</Fragment>
      ))}
      <ReloadWarningPopup />
    </ModalContext.Provider>
  );
};

export const useModal = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error("useModal must be used within an ModalProvider");
  }
  return context;
};
